import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthModal from './components/AuthModal';
import './App.css';
import './styles/theme.css';
import { PermissionsProvider } from './context/PermissionsContext';
import { useAnalytics } from './hooks/useAnalytics';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';

// Route-level code splitting: only Home ships in the first bundle.
const Builder = lazy(() => import('./pages/Builder'));
const ManufacturerSignup = lazy(() => import('./pages/ManufacturerSignup'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const BuilderNew = lazy(() => import('./pages/BuilderNew'));
const Order = lazy(() => import('./pages/Order'));
const GroupCheckout = lazy(() => import('./pages/GroupCheckout'));
const MyDesigns = lazy(() => import('./pages/MyDesigns'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const MyOrders = lazy(() => import('./pages/MyOrders'));
const ManufacturerQueue = lazy(() => import('./pages/ManufacturerQueue'));
const ManufacturerPortal = lazy(() => import('./pages/ManufacturerPortal'));
const ManufacturerLogin = lazy(() => import('./pages/ManufacturerLogin'));
const CustomOrder = lazy(() => import('./pages/CustomOrder'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const BlogAuthor = lazy(() => import('./pages/BlogAuthor'));
const BlogTag = lazy(() => import('./pages/BlogTag'));
const BlogUnsubscribe = lazy(() => import('./pages/BlogUnsubscribe'));
const ExpertRequest = lazy(() => import('./pages/ExpertRequest'));
// The old standalone review page is retired: proof review, quantity and shipping all live on the checkout page
const ApproveToCheckout = () => {
  const { orderNumber } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/custom-checkout/${orderNumber}${search}`} replace />;
};
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const ChatWidget = lazy(() => import('./components/ChatWidget'));

const BuilderNewWrapper = (props) => {
  const location = useLocation();
  return <BuilderNew key={location.search} {...props} />;
};


const Navbar = ({ showAuth, setShowAuth }) => {
  const { user, logout, dbUser } = useAuth();

  // Redirect manufacturer accounts to their queue
  useEffect(() => {
    if (dbUser?.role === 'manufacturer') {
      const path = window.location.pathname;
      if (path === '/' || path === '/my-orders' || path === '/designs') {
        navigate('/manufacturer');
      }
    }
  }, [dbUser]);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showProductsMenu, setShowProductsMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // The full link row needs about 1100px; narrower screens (small laptops, tablets) get the menu button
  const [compactNav, setCompactNav] = useState(window.innerWidth <= 1100);

  useEffect(() => {
    const handler = () => { setIsMobile(window.innerWidth <= 768); setCompactNav(window.innerWidth <= 1100); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    const loadUnread = async () => {
      if (!user) return;
      try {
        const token = await getToken();
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.unreadCount) setUnreadMessages(data.unreadCount);
      } catch (err) {}
    };
    loadUnread();
    const interval = setInterval(loadUnread, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const navLinks = [
    { to: '/builder', label: 'AI Builder', icon: true },
    { to: '/custom-order', label: 'Custom Request' },
    { to: '/how-it-works', label: 'How It Works' },
    { to: '/faq', label: 'FAQ' },
    { to: '/blog', label: 'Blog' },
  ];

  const PRODUCT_ITEMS = [
    { to: '/products/lapel-pins', label: 'Lapel Pins', desc: 'Soft, hard enamel & die struck', image: '/images/lapelPin_builder.png' },
    { to: '/products/trading-pins', label: 'Trading Pins', desc: 'Baseball, softball & collectibles', image: '/images/tradingPin_builder.png' },
    { to: '/products/medals', label: 'Medals', desc: 'Sports, award & recognition', image: '/images/medal_builder.png' },
    { to: '/products/coins', label: 'Challenge Coins', desc: 'Military, corporate & commemorative', image: '/images/coin_builder.png' },
    { to: '/products/pvc-patches', label: 'PVC Patches', desc: 'Military, morale & tactical', image: '/images/pvcPatch_builder.png' },
    { to: '/products/keychains', label: 'Keychains', desc: 'Promotional & souvenir', image: '/images/keyChain_builder.png' },
    { to: '/products/charms', label: 'Charms', desc: 'Jewelry, zipper & bag charms', image: '/images/charm_builder.png' },
    { to: '/products/belt-buckles', label: 'Belt Buckles', desc: 'Western, award & championship', image: '/images/beltBuckle_builder.png' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <HelmetProvider>
      <nav style={{
        background: scrolled ? 'rgba(255,255,255,0.80)' : 'rgba(255,255,255,0.97)',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(27,58,140,0.08)' : '1px solid var(--border-light)',
        boxShadow: scrolled ? '0 2px 16px rgba(27,58,140,0.06)' : '0 1px 12px rgba(0,0,0,0.06)',
        transition: 'background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
        position: 'fixed', top: 0, left: 0, right: 0,
        zIndex: 9990,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', height: isMobile ? 60 : (scrolled ? 68 : 80), gap: 8, transition: 'height 0.25s ease' }}>

            {/* Logo */}
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', marginRight: compactNav ? 0 : 40, flex: compactNav ? 1 : 'none' }}>
              <img
                fetchPriority="high"
                src="/images/logo.svg"
                alt="BrandsterPins"
                style={{ height: isMobile ? 36 : (scrolled ? 44 : 68), width: 'auto', display: 'block', transition: 'height 0.25s ease, opacity 0.3s ease', visibility: showMobileMenu ? 'hidden' : 'visible' }}
              />
            </Link>

            {/* Desktop center nav */}
            {!compactNav && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flex: 1 }}>
                {/* Products dropdown */}
                <div style={{ position: 'relative' }}
                  onMouseEnter={() => setShowProductsMenu(true)}
                  onMouseLeave={() => setShowProductsMenu(false)}>
                  <Link to="/products" style={{
                    padding: '8px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                    textDecoration: 'none', transition: 'all 0.15s',
                    color: location.pathname.startsWith('/products') ? 'var(--brand-blue)' : 'var(--ink-700)',
                    background: location.pathname.startsWith('/products') ? 'var(--brand-blue-tint)' : 'transparent',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    Products
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      style={{ transform: showProductsMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </Link>
                  {showProductsMenu && (
                    <div style={{
                      position: 'absolute', top: '100%', left: -80, zIndex: 100,
                      background: 'white', border: '1px solid var(--border-light)',
                      borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                      width: 520, overflow: 'hidden',
                      paddingTop: 8,
                    }}>
                      {/* Invisible bridge to prevent gap mouseLeave */}
                      <div style={{ position: 'absolute', top: -8, left: 0, right: 0, height: 8, background: 'transparent' }} />
                      {/* Arrow */}
                      <div style={{ position: 'absolute', top: 2, left: 140, width: 12, height: 12, background: 'white', border: '1px solid var(--border-light)', borderRight: 'none', borderBottom: 'none', transform: 'rotate(45deg)' }} />

                      {/* Header */}
                      <div style={{ padding: '18px 24px 12px', borderBottom: '1px solid var(--border-light)' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>Custom Promotional Products</div>
                      </div>

                      {/* Product list — 2 columns */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '8px 12px 12px' }}>
                        {PRODUCT_ITEMS.map(item => (
                          <Link key={item.to} to={item.to}
                            onClick={() => setShowProductsMenu(false)}
                            style={{ display: 'flex', flexDirection: 'column', padding: '10px 12px', borderRadius: 12, textDecoration: 'none', transition: 'all 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-blue-tint)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-900)', letterSpacing: '-0.01em' }}>{item.label}</div>
                            <div style={{ fontSize: 11, color: 'var(--ink-400)', marginTop: 2, lineHeight: 1.4 }}>{item.desc}</div>
                          </Link>
                        ))}
                      </div>

                      {/* Footer */}
                      <div style={{ borderTop: '1px solid var(--border-light)', padding: '12px 24px', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Link to="/products" onClick={() => setShowProductsMenu(false)} style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-blue)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                          View all products
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </Link>
                        <Link to="/builder" onClick={() => setShowProductsMenu(false)} style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-blue)', background: 'var(--brand-blue-tint)', textDecoration: 'none', padding: '7px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                          Try AI Builder
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                {navLinks.map(link => (
                  <Link key={link.to} to={link.to} style={{
                    padding: '8px 14px', borderRadius: 8,
                    fontSize: 14, fontWeight: 600,
                    textDecoration: 'none', transition: 'all 0.2s',
                    color: isActive(link.to) ? 'var(--brand-blue)' : 'var(--ink-700)',
                    background: isActive(link.to) ? 'var(--brand-blue-tint)' : 'transparent',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}>
                    {link.icon && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></svg>}
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Desktop right side */}
            {!compactNav && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {user ? (
                  <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowMenu(!showMenu)} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 12px 7px 7px', borderRadius: 12,
                      border: '1.5px solid var(--border)', background: 'white',
                      cursor: 'pointer', transition: 'all 0.15s',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-blue)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg, #1B3A8C, #2952CC)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                        {(user.displayName || user.email || '?')[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.displayName?.split(' ')[0] || 'Account'}
                      </span>
                      {unreadMessages > 0 && (
                        <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--brand-red)', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadMessages}</span>
                      )}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ink-400)" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
                    </button>
                    {showMenu && (
                      <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100, background: 'white', border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.12)', minWidth: 200, padding: 8 }}>
                        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-900)' }}>{user.displayName || 'Account'}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink-400)', marginTop: 2 }}>{user.email}</div>
                        </div>
                        {[
                          { to: '/my-designs', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', label: 'My Designs' },
                          ...(dbUser?.role !== 'manufacturer' ? [{ to: '/my-orders', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label: 'My Orders', badge: unreadMessages }] : []),
                          ...(dbUser?.role === 'manufacturer' ? [{ to: '/manufacturer', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', label: 'My Orders' }] : []),
                          ...(dbUser?.role && !['customer', 'manufacturer'].includes(dbUser.role) ? [{ to: '/admin', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', label: 'Admin' }] : []),
                        ].map(item => (
                          <Link key={item.to} to={item.to} onClick={() => setShowMenu(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 8, textDecoration: 'none', color: 'var(--ink-700)', fontSize: 13, fontWeight: 500, transition: 'all 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d={item.icon}/></svg>
                            {item.label}
                            {item.badge > 0 && <span style={{ marginLeft: 'auto', width: 18, height: 18, borderRadius: '50%', background: 'var(--brand-red)', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.badge}</span>}
                          </Link>
                        ))}
                        <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
                        <button onClick={async () => { setShowMenu(false); await logout(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 14px', borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--brand-red)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.1s', fontFamily: 'Inter, sans-serif' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => setShowAuth(true)} style={{ width: 38, height: 38, borderRadius: 10, border: '1.5px solid var(--border)', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
                    title="Sign in"
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-blue)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--ink-600)" strokeWidth="1.8" strokeLinecap="round">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </button>
                    <Link to="/custom-order" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, background: 'var(--brand-blue)', color: 'white', textDecoration: 'none', fontSize: 14, fontWeight: 700, transition: 'all 0.18s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--brand-blue-dark)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--brand-blue)'; e.currentTarget.style.transform = 'none'; }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(27,58,140,0.35)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(27,58,140,0.25)'; }}>
                      Get a Free Quote
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Mobile right side */}
            {compactNav && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {user && unreadMessages > 0 && (
                  <Link to="/my-orders" style={{ position: 'relative', textDecoration: 'none' }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--brand-red)', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadMessages}</div>
                  </Link>
                )}
                <button onClick={() => setShowMobileMenu(prev => !prev)} aria-label={showMobileMenu ? 'Close menu' : 'Open menu'} aria-expanded={!!showMobileMenu} style={{ width: 40, height: 40, borderRadius: 10, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)"  strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {showMobileMenu && (
        <>
        {/* Backdrop — tap to close */}
        <div onClick={() => setShowMobileMenu(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 199, backdropFilter: 'blur(2px)' }} />
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'white', zIndex: 200, display: 'flex', flexDirection: 'column', overflowY: 'auto', width: '85%', maxWidth: 360, boxShadow: '4px 0 24px rgba(0,0,0,0.15)' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-light)' }}>
            <Link to="/" onClick={() => setShowMobileMenu(false)}>
              <img src="/images/logo.svg" alt="BrandsterPins" style={{ height: 36, width: 'auto', display: 'block' }} />
            </Link>
            <button onClick={() => setShowMobileMenu(false)} style={{ width: 38, height: 38, borderRadius: 10, border: '1.5px solid var(--border)', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-700)" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>

          <div style={{ flex: 1, padding: '16px 16px' }}>
            {/* User info */}
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, background: 'var(--surface)', marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #1B3A8C, #2952CC)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'white' }}>
                  {(user.displayName || user.email || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-900)' }}>{user.displayName || 'Account'}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-400)' }}>{user.email}</div>
                </div>
              </div>
            )}

            {/* Nav links */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-400)', padding: '0 16px', marginBottom: 6 }}>Navigation</div>
              {navLinks.map(link => (
                <Link key={link.to} to={link.to} onClick={() => setShowMobileMenu(false)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '13px 16px', borderRadius: 10, textDecoration: 'none',
                  color: isActive(link.to) ? 'var(--brand-blue)' : 'var(--ink-700)',
                  fontSize: 15, fontWeight: isActive(link.to) ? 700 : 500,
                  background: isActive(link.to) ? '#EEF2FC' : 'transparent',
                  borderLeft: isActive(link.to) ? '3px solid var(--brand-blue)' : '3px solid transparent',
                  marginBottom: 2,
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {link.icon && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></svg>}
                    {link.label}
                  </span>
                  {isActive(link.to) && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-blue)" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>}
                </Link>
              ))}
            </div>

            {/* Products */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-400)', padding: '0 16px', marginBottom: 6 }}>Products</div>
              {PRODUCT_ITEMS.map(item => (
                <Link key={item.to} to={item.to} onClick={() => setShowMobileMenu(false)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '13px 16px', borderRadius: 10, textDecoration: 'none',
                  color: isActive(item.to) ? 'var(--brand-blue)' : 'var(--ink-700)',
                  fontSize: 15, fontWeight: isActive(item.to) ? 700 : 500,
                  background: isActive(item.to) ? '#EEF2FC' : 'transparent',
                  borderLeft: isActive(item.to) ? '3px solid var(--brand-blue)' : '3px solid transparent',
                  marginBottom: 2,
                }}>
                  {item.label}
                  {isActive(item.to) && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-blue)" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>}
                </Link>
              ))}
            </div>

            {/* Account links for logged in */}
            {user && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-400)', padding: '0 16px', marginBottom: 6 }}>Account</div>
                <Link to="/my-designs" onClick={() => setShowMobileMenu(false)} style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', borderRadius: 10, textDecoration: 'none', color: 'var(--ink-700)', fontSize: 15, fontWeight: 500, marginBottom: 2 }}>My Designs</Link>
                <Link to={dbUser?.role === 'manufacturer' ? '/manufacturer' : '/my-orders'} onClick={() => setShowMobileMenu(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderRadius: 10, textDecoration: 'none', color: 'var(--ink-700)', fontSize: 15, fontWeight: 500, marginBottom: 2 }}>
                  {dbUser?.role === 'manufacturer' ? 'Order Queue' : 'My Orders'}
                  {unreadMessages > 0 && <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--brand-red)', color: 'white', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadMessages}</span>}
                </Link>
                {dbUser?.role && !['customer', 'manufacturer'].includes(dbUser.role) && <Link to="/admin" onClick={() => setShowMobileMenu(false)} style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', borderRadius: 10, textDecoration: 'none', color: 'var(--ink-700)', fontSize: 15, fontWeight: 500 }}>Admin</Link>}
              </div>
            )}
          </div>

          {/* Bottom CTAs */}
          <div style={{ padding: '16px', borderTop: '1px solid var(--border-light)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {user ? (
              <button onClick={async () => { setShowMobileMenu(false); await logout(); navigate('/'); }} style={{ padding: '14px', borderRadius: 12, border: '1.5px solid #FEE2E2', background: '#FEF2F2', color: '#DC2626', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                Sign out
              </button>
            ) : (
              <>
                <button onClick={() => { setShowMobileMenu(false); setShowAuth(true); }} style={{ padding: '14px', borderRadius: 12, border: '1.5px solid var(--border)', background: 'white', color: 'var(--ink-700)', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                  Sign in
                </button>
                <Link to="/custom-order" onClick={() => setShowMobileMenu(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px', borderRadius: 12, background: 'var(--brand-blue)', color: 'white', textDecoration: 'none', fontSize: 15, fontWeight: 700 }}>
                  Get a Free Quote
                </Link>
              </>
            )}
          </div>
        </div>
        </>
      )}
    </HelmetProvider>
  );
};


const AppContent = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const appLocation = useLocation();
  const isHome = appLocation.pathname === '/';
  const [showAuth, setShowAuth] = useState(false);
  const location = useLocation();
  const hideChat = location.pathname === '/my-orders';

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const { track } = useAnalytics(dbUser?.id || null);

  useEffect(() => {
    if (user) {
      const pending = localStorage.getItem('pendingCustomOrder');
      if (pending) {
        navigate('/custom-order');
      }
    }
  }, [user]);

  return (
    <HelmetProvider>
      <Navbar showAuth={showAuth} setShowAuth={setShowAuth} />
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} onSuccess={() => {
        setShowAuth(false);
        let redirect = null;
        try {
          redirect = sessionStorage.getItem('postLoginRedirect');
          sessionStorage.removeItem('postLoginRedirect');
        } catch {}
        if (redirect) navigate(redirect);
      }} />



      <div style={{ paddingTop: isMobile ? (isHome ? 0 : 60) : 80, background: 'transparent' }}>
      <div key={location.pathname} style={isMobile ? {
        animation: 'pageFadeIn 0.4s ease forwards',
      } : {}}>
      <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/products/:slug" element={<ProductDetail />} />
        <Route path="/builder" element={<BuilderNewWrapper onSignInRequest={() => setShowAuth(true)} />} />
        <Route path="/manufacturer/signup" element={<ManufacturerSignup />} />
        <Route path="/manufacturer/view/:orderNumber" element={<ManufacturerPortal />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/order/:designId" element={<ProtectedRoute showAuthInstead onShowAuth={() => setShowAuth(true)}><Order /></ProtectedRoute>} />
        <Route path="/my-designs" element={<ProtectedRoute showAuthInstead onShowAuth={() => setShowAuth(true)}><MyDesigns /></ProtectedRoute>} />
        <Route path="/admin/*" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/my-orders" element={<ProtectedRoute showAuthInstead onShowAuth={() => setShowAuth(true)}><MyOrders /></ProtectedRoute>} />
        <Route path="/manufacturer" element={<ProtectedRoute showAuthInstead onShowAuth={() => setShowAuth(true)}><ManufacturerQueue /></ProtectedRoute>} />
        <Route path="/manufacturer/login" element={<ManufacturerLogin />} />
        <Route path="/manufacturer/order/:token" element={<ManufacturerPortal />} />
        <Route path="/artwork-action" element={<Navigate to={'/my-orders' + window.location.search} replace />} />
        <Route path="/custom-order" element={<CustomOrder onSignInRequest={() => setShowAuth(true)} />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/author/:slug" element={<BlogAuthor />} />
        <Route path="/blog/tag/:slug" element={<BlogTag />} />
        <Route path="/blog/unsubscribe" element={<BlogUnsubscribe />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/expert-request/:designId" element={<ExpertRequest onSignInRequest={() => setShowAuth(true)} />} />
        <Route path="/custom-order/:orderNumber/approve" element={<ApproveToCheckout />} />
        <Route path="/custom-checkout/group/:groupId" element={<ProtectedRoute showAuthInstead onShowAuth={() => setShowAuth(true)}><GroupCheckout /></ProtectedRoute>} />
        <Route path="/custom-checkout/:orderNumber" element={<ProtectedRoute showAuthInstead onShowAuth={() => setShowAuth(true)}><Order /></ProtectedRoute>} />
      </Routes>
      </Suspense>
      </div>
      </div>
      <Suspense fallback={null}><ChatWidget hideOnOrderPage={hideChat} /></Suspense>

    </HelmetProvider>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <PermissionsProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      </PermissionsProvider>
    </AuthProvider>
  );
};

export default App;